/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useAuth, useFeatureFlags } from '@kaiizen/kai-pro-api-react-state';

import { COLORS } from './closeoutTokens';

import { useScanEditingContextSlice } from '@/Client/State';
import { OrgSettingsIcon } from '@kaiizen/kai-shiki/react';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { MAIN_NAVIGATION_BUTTON_STYLES } from '../MainNavigation/mainNavigationStyles';
import {
  buildCloseoutApprovalSummary,
  CloseoutApprovalModal,
} from './CloseoutApprovalModal';
import {
  ALL_CONTRACTORS_ID,
  buildContractorFilterOptions,
  type ContractorFilterOption,
  getContractorFilterTaskCount,
} from './contractorFilters';

const ACTIONS_STYLE = css`
  label: CLOSEOUT_REVIEW_HEADER_ACTIONS_STYLE;
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;

const APPROVE_BUTTON_STYLE = css`
  label: CLOSEOUT_REVIEW_APPROVE_CLOSEOUT_BUTTON_STYLE;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 18px;
  border: 1px solid #60956b;
  border-radius: 0;
  background: #60956b;
  color: #fff;
  font-size: ${pxToRem(13)};
  font-weight: 800;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background: #56865f;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
    background: #60956b;
  }

  &:disabled:hover {
    background: #60956b;
  }
`;

const FILTER_BUTTON_STYLE = css`
  label: CLOSEOUT_REVIEW_CONTRACTOR_FILTER_BUTTON_STYLE;
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #5f8ed8;
  border-radius: 0;
  background: transparent;
  color: ${COLORS.paper};
  box-shadow: none;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
  }
`;

const MENU_STYLE = css`
  label: CLOSEOUT_REVIEW_CONTRACTOR_FILTER_MENU_STYLE;
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 500;
  width: 318px;
  max-height: min(520px, calc(100vh - 96px));
  overflow-y: auto;
  border: 1px solid ${COLORS.stroke};
  border-radius: 0;
  background: ${COLORS.white};
  color: ${COLORS.ink};
  box-shadow: 0 18px 36px rgba(29, 29, 29, 0.18);
  padding: 0;
`;

const OPTION_STYLE = css`
  label: CLOSEOUT_REVIEW_CONTRACTOR_FILTER_OPTION_STYLE;
  width: 100%;
  min-height: 56px;
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr) auto;
  column-gap: 12px;
  align-items: center;
  border: 0;
  border-bottom: 1px solid #e6e0d8;
  background: ${COLORS.white};
  color: ${COLORS.ink};
  padding: 10px 14px;
  text-align: left;
  font: inherit;
  cursor: pointer;

  &:last-of-type {
    border-bottom: 0;
  }

  &:hover {
    background: ${COLORS.paper};
  }

  &[aria-checked='true'] {
    background: ${COLORS.surfaceMuted};
  }
`;

const RADIO_DOT_STYLE = css`
  label: CLOSEOUT_REVIEW_CONTRACTOR_FILTER_RADIO_DOT_STYLE;
  width: 10px;
  height: 10px;
  border-radius: 0;
  background: ${COLORS.stroke};
`;

const SELECTED_DOT_STYLE = css`
  background: #5e9467;
`;

const OPTION_TITLE_ROW_STYLE = css`
  label: CLOSEOUT_REVIEW_CONTRACTOR_FILTER_OPTION_TITLE_ROW_STYLE;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const OPTION_TITLE_STYLE = css`
  label: CLOSEOUT_REVIEW_CONTRACTOR_FILTER_OPTION_TITLE_STYLE;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${pxToRem(14)};
  font-weight: 600;
  line-height: 1.25;
`;

const OPTION_META_STYLE = css`
  label: CLOSEOUT_REVIEW_CONTRACTOR_FILTER_OPTION_META_STYLE;
  margin-top: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${COLORS.midtone};
  font-size: ${pxToRem(12)};
  line-height: 1.2;
  white-space: nowrap;
`;

const INITIALS_STYLE = css`
  label: CLOSEOUT_REVIEW_CONTRACTOR_FILTER_INITIALS_STYLE;
  flex: 0 0 auto;
  min-width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0;
  background: ${COLORS.surfaceMuted};
  color: #5c8a62;
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.02em;
`;

const TASK_COUNT_STYLE = css`
  label: CLOSEOUT_REVIEW_CONTRACTOR_FILTER_TASK_COUNT_STYLE;
  color: ${COLORS.midtone};
  font-size: ${pxToRem(13)};
  letter-spacing: 0.08em;
  white-space: nowrap;
`;

const getTaskCountLabel = (taskCount: number): string => `${taskCount} ${taskCount === 1 ? 'task' : 'tasks'}`;

const getInitials = (label?: string): string | undefined => {
  const words = label?.match(/[A-Za-z0-9]+/g);
  if (!words?.length) return undefined;
  return words.slice(0, 2).map(word => word[0]?.toUpperCase()).join('');
};

const OptionButton = ({
  checked,
  countLabel,
  initials,
  label,
  meta,
  onSelect,
}: {
  checked: boolean;
  countLabel?: string;
  initials?: string;
  label: string;
  meta?: string;
  onSelect: () => void;
}) => (
  <button
    type='button'
    role='menuitemradio'
    aria-checked={checked}
    aria-label={[label, countLabel].filter(Boolean).join(', ')}
    css={OPTION_STYLE}
    onClick={onSelect}
  >
    <span css={[RADIO_DOT_STYLE, checked && SELECTED_DOT_STYLE]} aria-hidden='true' />
    <span style={{ minWidth: 0 }}>
      <span css={OPTION_TITLE_ROW_STYLE}>
        <span css={OPTION_TITLE_STYLE}>{label}</span>
        {initials ? <span css={INITIALS_STYLE}>{initials}</span> : null}
      </span>
      {meta ? <span css={OPTION_META_STYLE}>{meta}</span> : null}
    </span>
    {countLabel ? <span css={TASK_COUNT_STYLE}>{countLabel}</span> : null}
  </button>
);

const ContractorOptionButton = ({
  option,
  selectedContractorId,
  onSelectContractor,
}: {
  option: ContractorFilterOption;
  selectedContractorId: string;
  onSelectContractor: (contractorId: string) => void;
}) => (
  <OptionButton
    checked={selectedContractorId === option.id}
    countLabel={getTaskCountLabel(option.taskCount)}
    initials={getInitials(option.trade ?? option.name)}
    label={option.name}
    meta={option.trade}
    onSelect={() => onSelectContractor(option.id)}
  />
);

type ContractorFilterHeaderActionProps = {
  selectedContractorId?: string;
  onSelectContractor: (contractorId: string) => void;
};

type ApproveCloseoutHeaderActionProps = {
  onApproveCloseout: () => void | Promise<void>;
  onRequestCloseoutEdit: () => void | Promise<void>;
  allowTurnBasedDirectApproval?: boolean;
  newScopeBuilderFlow?: boolean;
  disabled?: boolean;
};

export const ContractorFilterHeaderAction = ({
  selectedContractorId = ALL_CONTRACTORS_ID,
  onSelectContractor,
}: ContractorFilterHeaderActionProps) => {
  const {
    getContractorForTask,
    scanRoot,
  } = useScanEditingContextSlice([
    'getContractorForTask',
    'scanRoot',
  ]);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const scanGroups = useMemo(
    () => scanRoot?.groups ?? [],
    [scanRoot],
  );
  const options = useMemo(
    () =>
      buildContractorFilterOptions(scanGroups, {
        getContractorForTask,
      }),
    [scanGroups, getContractorForTask],
  );
  const allTaskCount = getContractorFilterTaskCount(options);

  useEffect(() => {
    if (!open) return;

    const closeOnOutsidePointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutsidePointer);
    return () => document.removeEventListener('mousedown', closeOnOutsidePointer);
  }, [open]);

  const selectContractor = (contractorId: string) => {
    onSelectContractor(contractorId);
    setOpen(false);
  };

  return (
    <div css={ACTIONS_STYLE} ref={rootRef} data-testid='closeout-contractor-filter-action' onClick={event => event.stopPropagation()}>
      <button
        type='button'
        css={FILTER_BUTTON_STYLE}
        aria-label='Filter by contractor'
        aria-haspopup='menu'
        aria-expanded={open}
        title='Filter by contractor'
        onClick={() => setOpen(previous => !previous)}
      >
        <OrgSettingsIcon data-testid='closeout-contractor-filter-icon' size={28} color='currentColor' />
      </button>
      {open
        ? (
          <div css={MENU_STYLE} role='menu' data-testid='closeout-contractor-filter-menu'>
            <OptionButton
              checked={selectedContractorId === ALL_CONTRACTORS_ID}
              countLabel={getTaskCountLabel(allTaskCount)}
              label='All contractors'
              onSelect={() => selectContractor(ALL_CONTRACTORS_ID)}
            />
            {options.map(option => (
              <ContractorOptionButton
                key={option.id}
                option={option}
                selectedContractorId={selectedContractorId}
                onSelectContractor={selectContractor}
              />
            ))}
          </div>
        )
        : null}
    </div>
  );
};

export const ApproveCloseoutHeaderAction = (
  { onApproveCloseout, onRequestCloseoutEdit, allowTurnBasedDirectApproval = false, newScopeBuilderFlow, disabled = false }: ApproveCloseoutHeaderActionProps,
) => {
  const { userId } = useAuth();
  const { featureFlags } = useFeatureFlags();
  const {
    approvalStatusByTaskId,
    effectiveScanType,
    property,
    project,
    scan,
    scanRoot,
  } = useScanEditingContextSlice([
    'approvalStatusByTaskId',
    'effectiveScanType',
    'property',
    'project',
    'scan',
    'scanRoot',
  ]);
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [requestingEdit, setRequestingEdit] = useState(false);
  const approvalSummary = useMemo(
    () =>
      buildCloseoutApprovalSummary({
        address: property?.address,
        approvalStatusByTaskId,
        scanRoot,
      }),
    [approvalStatusByTaskId, property?.address, scanRoot],
  );

  const canApproveTurnBasedCloseout = featureFlags.turnBasedHandoff
    && effectiveScanType === 'progressCompleted'
    && !!project
    && !!userId
    && project.managerIds.includes(userId);
  if (allowTurnBasedDirectApproval && !canApproveTurnBasedCloseout) return null;

  const approveCloseout = async () => {
    setApproving(true);
    try {
      await onApproveCloseout();
      setApprovalModalOpen(false);
    } finally {
      setApproving(false);
    }
  };

  const requestCloseoutEdit = async () => {
    setRequestingEdit(true);
    try {
      await onRequestCloseoutEdit();
      setApprovalModalOpen(false);
    } finally {
      setRequestingEdit(false);
    }
  };

  return (
    <div css={ACTIONS_STYLE} data-testid='closeout-approval-action' onClick={event => event.stopPropagation()}>
      <button
        type='button'
        css={newScopeBuilderFlow ? MAIN_NAVIGATION_BUTTON_STYLES.primary : APPROVE_BUTTON_STYLE}
        onClick={() => setApprovalModalOpen(true)}
        disabled={disabled}
      >
        Approve Closeout
      </button>
      {approvalModalOpen
        ? (
          <CloseoutApprovalModal
            approving={approving || requestingEdit}
            summary={approvalSummary}
            onApprove={approveCloseout}
            onCancel={() => setApprovalModalOpen(false)}
            onRequestEdit={requestCloseoutEdit}
          />
        )
        : null}
    </div>
  );
};
